import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  SettingsWell,
  SettingsListCard,
  SettingsList,
  SettingsListRow,
  settingsButtonClass,
} from '@/components/shared/settings/SettingsKit'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { copyTextToClipboard } from '@/lib/utils/clipboard'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { CreateOrUpgradeButton } from '@/components/shared/plan-usage/CreateOrUpgradeButton'
import { useWorkspace } from '@/lib/contexts/WorkspaceContext'
import { createMagicLink, getMagicLinks, revokeMagicLink } from '@/lib/api/share'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Loading01Icon,
} from '@hugeicons/core-free-icons'

export default function ShareLinksSettingsTab() {
  const { token } = useAuth()
  const { propertyId } = useWorkspace()
  // View-only accounts keep and revoke existing links; creating one is paywalled.
  const { isReadOnly } = useSubscription()
  const queryClient = useQueryClient()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [expiry, setExpiry] = useState<'24-hours' | '2-weeks' | 'forever'>('2-weeks')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const { data: allLinks = [], isLoading } = useQuery({
    queryKey: ['magicLinks'],
    queryFn: () => getMagicLinks(token!),
    enabled: !!token,
  })

  const shareLinks = allLinks.filter((link) => link.propertyId === propertyId)

  const createMutation = useMutation({
    mutationFn: () => {
      if (!propertyId || !token) throw new Error('Missing property or auth')
      return createMagicLink(propertyId, expiry, token)
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['magicLinks'] })
      const copied = await copyTextToClipboard(data.link)
      toast.success(
        copied
          ? 'Share link created and copied to clipboard'
          : 'Share link created — copy it from the list below'
      )
      setIsCreateDialogOpen(false)
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create share link')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (magicLinkId: string) => revokeMagicLink(propertyId!, magicLinkId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['magicLinks'] })
      toast.success('Share link revoked')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to revoke share link')
    },
  })

  const copyLink = async (link: string, id: string) => {
    const copied = await copyTextToClipboard(link)
    if (copied) {
      setCopiedId(id)
      toast.success('Link copied to clipboard')
      setTimeout(() => setCopiedId(null), 2000)
    } else {
      toast.error('Failed to copy link')
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <SettingsWell>
      <SettingsListCard
        title="Share links"
        description="Read-only dashboard links for clients or teammates, with an expiry."
        action={
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <CreateOrUpgradeButton
                  atLimit={isReadOnly}
                  noun="share links"
                  variant="outline"
                  className={settingsButtonClass}
                  disabled={!propertyId}
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  Create link
                </CreateOrUpgradeButton>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-base font-medium">Create share link</DialogTitle>
                    <DialogDescription className="text-[13px]">
                      Anyone with the link can view this site's dashboard.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <Label className="text-[12px] font-medium">Link expiration</Label>
                    <RadioGroup
                      value={expiry}
                      onValueChange={(v) => setExpiry(v as typeof expiry)}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="24-hours" id="exp-24h" />
                        <Label htmlFor="exp-24h" className="font-normal cursor-pointer text-[13px]">
                          24 hours
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2-weeks" id="exp-2w" />
                        <Label htmlFor="exp-2w" className="font-normal cursor-pointer text-[13px]">
                          2 weeks
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="forever" id="exp-forever" />
                        <Label
                          htmlFor="exp-forever"
                          className="font-normal cursor-pointer text-[13px]"
                        >
                          Never expires
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => createMutation.mutate()}
                      disabled={createMutation.isPending}
                      className="gap-1.5"
                    >
                      {createMutation.isPending ? (
                        <>
                          <HugeiconsIcon
                            icon={Loading01Icon}
                            size={14}
                            className="animate-spin"
                          />
                          Creating…
                        </>
                      ) : (
                        'Create & copy link'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <HugeiconsIcon
              icon={Loading01Icon}
              size={16}
              className="animate-spin text-muted-foreground"
            />
          </div>
        ) : shareLinks.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] leading-5 text-foreground/60">
            No share links yet. Create one to show a client or teammate this site's analytics.
          </p>
        ) : (
          <SettingsList>
            {shareLinks.map((link) => (
              <SettingsListRow
                key={link.magicLinkId}
                primary={<span className="font-mono text-xs font-normal">{link.link}</span>}
                secondary={`Expires ${link.forever ? 'never' : formatDate(link.expiryDate)}`}
                trailing={
                  <>
                    <button
                      type="button"
                      onClick={() => copyLink(link.link, link.magicLinkId)}
                      className="h-7 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {copiedId === link.magicLinkId ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(link.magicLinkId)}
                      disabled={deleteMutation.isPending}
                      className="h-7 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                    >
                      Revoke
                    </button>
                  </>
                }
              />
            ))}
          </SettingsList>
        )}
      </SettingsListCard>
    </SettingsWell>
  )
}
